import React from "react";
import ReactDOM from "react-dom";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  Fade,
  Backdrop,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const ConfirmModal = ({
  show,
  handleClose,
  handleConfirm,
  title = "Confirm Action",
  body,
  subtitle,
  isLoading = false,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isDelete = false,
  isSuccess = false,
  isInfo = false,
  showDontShowAgain = false,
  dontShowAgain = false,
  onDontShowAgainChange,
  confirmColor,
  maxWidth = 480,
}) => {
  const getThemeColor = () => {
    if (confirmColor) return confirmColor;
    if (isDelete) return "#E02D2D";
    if (isSuccess) return "#0A9D5A";
    if (isInfo) return "#2469F0";
    return "#7C3AED";
  };

  const themeColor = getThemeColor();

  React.useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, handleClose]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <>
      <Fade in={show}>
        <Backdrop
          open={show}
          onClick={handleClose}
          sx={{
            zIndex: 1300,
            bgcolor: "rgba(15, 23, 42, 0.45)",
          }}
        />
      </Fade>

      <Fade in={show}>
        <Box
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1301,
            width: { xs: "calc(100vw - 48px)", sm: maxWidth },
            maxWidth: "calc(100vw - 32px)",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="close"
              sx={{
                position: "absolute",
                top: 0,
                right: -50,
                width: 40,
                height: 40,
                bgcolor: "#FFFFFF",
                color: "#1A1A2E",
                border: "1px solid #E5E7EB",
                boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.18)",
                zIndex: 2,
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: "#F8FAFC",
                  transform: "scale(1.05)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: "20px",
                boxShadow: "0px 24px 64px rgba(0, 0, 0, 0.18)",
                overflow: "hidden",
                p: 3.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.35rem",
                  color: "#0F172A",
                  lineHeight: 1.3,
                  mb: subtitle || body ? 1.5 : 2.5,
                  pr: 3,
                }}
              >
                {title}
              </Typography>

              {subtitle && (
                <Typography
                  sx={{
                    color: "#64748B",
                    fontSize: "0.9rem",
                    mb: 1.5,
                  }}
                >
                  {subtitle}
                </Typography>
              )}

              {body && (
                <Typography
                  sx={{
                    color: "#475569",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                    mb: 2,
                  }}
                >
                  {body}
                </Typography>
              )}

              {showDontShowAgain && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dontShowAgain}
                      onChange={(e) =>
                        onDontShowAgainChange?.(e.target.checked)
                      }
                      size="small"
                      sx={{
                        color: "#CBD5E1",
                        "&.Mui-checked": { color: themeColor },
                        p: 0.5,
                        mr: 0.5,
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{ fontSize: "0.9rem", color: "#64748B" }}
                    >
                      Don't show again
                    </Typography>
                  }
                  sx={{ mb: 3, ml: -0.5 }}
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "flex-end",
                  mt: 3,
                }}
              >
                <Button
                  onClick={handleClose}
                  disabled={isLoading}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: "#0F172A",
                    borderColor: "#E2E8F0",
                    borderRadius: "10px",
                    px: 2.5,
                    py: 1,
                    "&:hover": {
                      borderColor: "#CBD5E1",
                      bgcolor: "#F8FAFC",
                    },
                  }}
                >
                  {cancelText}
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  variant="contained"
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    px: 2.5,
                    py: 1,
                    borderRadius: "10px",
                    bgcolor: themeColor,
                    color: "#FFFFFF",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: themeColor,
                      filter: "brightness(0.92)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isLoading ? "Processing..." : confirmText}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </>,
    document.body
  );
};

export default ConfirmModal;